import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { listNebs, getNeb, applyNeb, respondNeb, getMyNebs } from '../api/neb';
import LoginModal from '../components/LoginModal';

const CATEGORY_LABEL = {
  designer: '디자인',
  developer: '개발',
  marketer: '마케팅',
  planner: '기획',
  creator: '크리에이터',
};

const CATEGORY_SYMBOL = {
  designer: '✦',
  developer: '◈',
  marketer: '◉',
  planner: '◇',
  creator: '✧',
};

const DECISION_CONFIG = {
  core: {
    emoji: '🌟',
    label: 'Core 멤버',
    color: '#1a1a1a',
    bg: '#f9f9f0',
    desc: '넵의 핵심 멤버로 합류했어요!',
  },
  associate: {
    emoji: '✨',
    label: 'Associate 멤버',
    color: '#1a1a1a',
    bg: '#f0f4f9',
    desc: '넵의 어소시에이트로 합류했어요!',
  },
  waitlist: {
    emoji: '🌀',
    label: '대기 중',
    color: '#555',
    bg: '#f5f5f5',
    desc: '정원이 차서 대기 중이에요. 자리가 나면 알려드릴게요!',
  },
  reject: {
    emoji: '🌙',
    label: '아쉽게도...',
    color: '#555',
    bg: '#f5f5f5',
    desc: '이번엔 다른 넵이 더 잘 맞을 것 같아요.',
  },
  already: {
    emoji: '✓',
    label: '이미 멤버',
    color: '#555',
    bg: '#f5f5f5',
    desc: '이미 가입된 넵이에요.',
  },
};

const THINKING_LINES = [
  '지식 우물 퍼올리는 중',
  '내쫓을 궁리 중 (아님)',
  '엇 이런 귀한 분이!',
  '합격 도장 찾는 중...',
  '웃기는 개그 소재 찾는 중',
  '판단력 충전 중 ▓▓▓░░',
  '이 답변... 흠... (좋은 흠)',
  '몰래 박수 치는 중',
  '진지한 척하는 중',
  '탈락 도장도 찾아봤어요 (안 씀)',
  '넵 인류 연구 중',
  '별자리 운세 확인 중',
  '오늘 합격 운: 매우 좋음 (미확인)',
  '고민 중... 고민 중... (거의 다 함)',
  '운영진 회의 소집 중',
  '이미 결정했는데 있어 보이려고 기다리는 중',
];

const WAITLIST_LINES = [
  'Neb은 Nebula의 약자입니다. (맞음)',
  '성운은 새로운 별이 태어나는 곳이에요.',
  '지금 이 순간도 우주는 팽창 중...',
  '대기자 목록 1위입니다. (미확인)',
  'N  E  B  U  L  A',
  '자리가 나면 제일 먼저 알려드릴게요. 아마도.',
  '우주의 나이는 138억 년. 잠깐 기다리는 거 별거 아니죠?',
  '빛이 지구에 도달하는 데 8분 걸립니다. 기다림에 익숙해져요.',
  '∴ 당신은 곧 별이 됩니다 (수학적으로)',
  'WAITING . . . . . . .',
  'AI를 더 똑똑하게 만들고 있습니다.',
  '넵의 AI는 지금 열심히 성장 중이에요. 같이 기다려요.',
  '이 텍스트는 자동으로 바뀝니다. 신기하죠.',
  '대기 중인 동안 명함 한 번 더 다듬어보는 건 어떨까요?',
  '성운의 평균 지름은 수십 광년. 여긴 좀 더 좁아요.',
  '넵은 여러분을 기억하고 있어요.',
  '인내는 쓰고 합류는 달다.',
  'ㄴr는 ㅂr다ㅇr이야 — 어느 넵 대기자',
  '지금 몇 명이 대기 중인지는... 비밀이에요.',
  'nebula nebula nebula nebula',
  '우주에서 외로운 별은 없어요. 곧 만나요.',
  '커피 한 잔 하고 오세요. 아직 여기 있을게요.',
  '잠깐, 방금 자리 날 뻔 했어요. (안 났음)',
];

// ─── WaitlistRoller ───────────────────────────────────────────────

function WaitlistRoller() {
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIdx(i => (i + 1) % WAITLIST_LINES.length);
        setVisible(true);
      }, 350);
    }, 2800);
    return () => clearInterval(interval);
  }, []);

  return (
    <div style={{
      fontSize: 11, color: '#bbb', letterSpacing: '0.5px',
      height: 18, overflow: 'hidden',
      opacity: visible ? 1 : 0,
      transition: 'opacity 0.3s',
      textAlign: 'center',
    }}>
      {WAITLIST_LINES[idx]}
    </div>
  );
}

// ─── ThinkingBubble ───────────────────────────────────────────────

function ThinkingBubble() {
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIdx(i => (i + 1) % THINKING_LINES.length);
        setVisible(true);
      }, 300);
    }, 2200);
    return () => clearInterval(interval);
  }, []);

  return (
    <div style={{
      padding: '10px 14px', background: '#f4f4f4',
      borderRadius: '14px 14px 14px 4px',
      fontSize: 12, color: '#aaa',
      opacity: visible ? 1 : 0,
      transition: 'opacity 0.25s',
      minWidth: 80,
    }}>
      {THINKING_LINES[idx]}
    </div>
  );
}

// ─── NebCard ─────────────────────────────────────────────────────

function NebCard({ neb, onJoin, onView, myMemberships }) {
  const membership = myMemberships.find(m => m.nebId === neb.id);
  const isMember = !!membership;
  const symbol = CATEGORY_SYMBOL[neb.category] || '✦';
  const coreRatio = neb.maxCore > 0 ? neb.coreCount / neb.maxCore : 0;
  const assocRatio = neb.maxAssociate > 0 ? neb.assocCount / neb.maxAssociate : 0;
  const isFull = coreRatio >= 1 && assocRatio >= 1;

  return (
    <div style={{
      background: '#fff',
      border: isMember ? '1px solid #d0d0d0' : '1px solid #ebebeb',
      borderRadius: 16,
      padding: '24px 24px 20px',
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
      transition: 'box-shadow 0.15s',
    }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.07)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <span style={{ fontSize: 22, marginRight: 6 }}>{symbol}</span>
          <span style={{ fontSize: 17, fontWeight: 700, letterSpacing: '-0.3px' }}>{neb.name}</span>
        </div>
        <span style={{
          fontSize: 11, fontWeight: 600, color: '#666',
          background: '#f3f3f3', borderRadius: 20,
          padding: '3px 10px', letterSpacing: '0.3px',
        }}>
          {CATEGORY_LABEL[neb.category] || neb.category}
        </span>
      </div>

      <p style={{ fontSize: 13, color: '#555', margin: 0, lineHeight: 1.7 }}>
        {neb.description}
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <MiniBar label="Core" count={neb.coreCount} max={neb.maxCore} />
        <MiniBar label="Associate" count={neb.assocCount} max={neb.maxAssociate} />
      </div>

      {isMember ? (
        <button
          onClick={() => onView(neb, membership)}
          style={{
            marginTop: 4, padding: '10px 0',
            background: '#f7f7f7', color: '#333',
            border: '1px solid #e0e0e0', borderRadius: 10,
            fontSize: 13, fontWeight: 600,
            cursor: 'pointer', letterSpacing: '0.2px',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}
          onMouseEnter={e => e.currentTarget.style.background = '#efefef'}
          onMouseLeave={e => e.currentTarget.style.background = '#f7f7f7'}
        >
          <span style={{
            fontSize: 10, fontWeight: 700, color: '#fff',
            background: membership.memberType === 'core' ? '#1a1a1a' : '#666',
            borderRadius: 4, padding: '2px 6px', letterSpacing: '0.5px',
          }}>
            {membership.memberType === 'core' ? 'CORE' : 'ASSOC'}
          </span>
          넵 보기 →
        </button>
      ) : (
        <button
          onClick={() => onJoin(neb)}
          disabled={isFull}
          style={{
            marginTop: 4, padding: '10px 0',
            background: isFull ? '#f0f0f0' : '#000',
            color: isFull ? '#aaa' : '#fff',
            border: 'none', borderRadius: 10,
            fontSize: 13, fontWeight: 600,
            cursor: isFull ? 'not-allowed' : 'pointer',
            letterSpacing: '0.2px', transition: 'opacity 0.15s',
          }}
          onMouseEnter={e => { if (!isFull) e.currentTarget.style.opacity = '0.85'; }}
          onMouseLeave={e => e.currentTarget.style.opacity = '1'}
        >
          {isFull ? '정원 마감' : '넵 가입하기 ✦'}
        </button>
      )}
    </div>
  );
}

function MiniBar({ label, count, max }) {
  const pct = max > 0 ? Math.min((count / max) * 100, 100) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ fontSize: 11, color: '#999', width: 60, flexShrink: 0 }}>{label}</span>
      <div style={{ flex: 1, height: 4, background: '#f0f0f0', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{
          height: '100%', width: `${pct}%`,
          background: pct >= 100 ? '#ccc' : '#000',
          borderRadius: 4, transition: 'width 0.4s',
        }} />
      </div>
      <span style={{ fontSize: 11, color: '#aaa', width: 36, textAlign: 'right', flexShrink: 0 }}>
        {count}/{max}
      </span>
    </div>
  );
}

// ─── ResultScreen ─────────────────────────────────────────────────

function ResultScreen({ result, onClose }) {
  const cfg = DECISION_CONFIG[result.status] || DECISION_CONFIG.reject;
  const isApproved = result.status === 'core' || result.status === 'associate';

  return (
    <div style={{
      textAlign: 'center', padding: '20px 8px 8px',
      background: cfg.bg, borderRadius: 16,
    }}>
      <div style={{ fontSize: 48, marginBottom: 10, lineHeight: 1 }}>{cfg.emoji}</div>
      <div style={{ fontSize: 15, fontWeight: 700, color: cfg.color, marginBottom: 8 }}>
        {cfg.label}
      </div>
      <p style={{ fontSize: 13, color: '#555', lineHeight: 1.75, marginBottom: 4 }}>
        {result.message || cfg.desc}
      </p>
      {result.suggestedNeb && (
        <p style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>
          추천 넵: <strong>{result.suggestedNeb}</strong>
        </p>
      )}

      {/* 대기 중 롤러 */}
      {result.status === 'waitlist' && (
        <div style={{ margin: '16px 0 4px' }}>
          <WaitlistRoller />
        </div>
      )}

      <button
        onClick={onClose}
        style={{
          marginTop: 20, padding: '10px 28px',
          background: isApproved ? '#000' : '#333',
          color: '#fff', border: 'none', borderRadius: 10,
          fontSize: 13, fontWeight: 600, cursor: 'pointer',
        }}
      >
        {isApproved ? '넵 입장 ✦' : '닫기'}
      </button>
    </div>
  );
}

// ─── NebDetailModal ───────────────────────────────────────────────

function MemberRow({ member }) {
  const profile = member.extProfile || {};
  const typeLabel = member.memberType === 'core' ? 'Core' : 'Associate';
  const typeColor = member.memberType === 'core' ? '#1a1a1a' : '#666';

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 10,
      padding: '10px 0', borderBottom: '1px solid #f5f5f5',
    }}>
      <div style={{
        width: 32, height: 32, borderRadius: '50%',
        background: member.memberType === 'core' ? '#1a1a1a' : '#e8e8e8',
        color: member.memberType === 'core' ? '#fff' : '#666',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 12, fontWeight: 700, flexShrink: 0,
      }}>
        {typeLabel[0]}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
          {member.shortId ? (
            <a
              href={`/card/${member.shortId}`}
              target="_blank"
              rel="noreferrer"
              style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a', textDecoration: 'none' }}
            >
              {profile.role || member.shortId}
            </a>
          ) : (
            <span style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>
              {profile.role || '(역할 미상)'}
            </span>
          )}
          <span style={{
            fontSize: 10, fontWeight: 700, color: typeColor,
            border: `1px solid ${typeColor}`, borderRadius: 4,
            padding: '1px 5px', letterSpacing: '0.3px',
          }}>
            {typeLabel}
          </span>
        </div>
        {profile.skills && (
          <p style={{ fontSize: 11, color: '#888', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {profile.skills}
          </p>
        )}
      </div>
      {profile.availability && (
        <span style={{
          fontSize: 10, color: '#aaa', flexShrink: 0, paddingTop: 2,
        }}>
          {profile.availability}
        </span>
      )}
    </div>
  );
}

function NebDetailModal({ neb, myMembership, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const symbol = CATEGORY_SYMBOL[neb.category] || '✦';

  useEffect(() => {
    getNeb(neb.id)
      .then(d => setData(d))
      .catch(() => setData({ neb, members: [] }))
      .finally(() => setLoading(false));
  }, [neb]);

  const members = data?.members || [];
  const coreMembers = members.filter(m => m.memberType === 'core');
  const assocMembers = members.filter(m => m.memberType === 'associate');
  const nebData = data?.neb || neb;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 2000,
      background: 'rgba(0,0,0,0.35)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '20px 16px',
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: '#fff', borderRadius: 20,
        width: '100%', maxWidth: 420, maxHeight: '85vh',
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 8px 40px rgba(0,0,0,0.14)',
      }}>
        {/* 헤더 */}
        <div style={{
          padding: '18px 20px 14px',
          borderBottom: '1px solid #f0f0f0',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <span style={{ fontSize: 15, fontWeight: 700 }}>{symbol} {nebData.name}</span>
            <span style={{
              fontSize: 10, fontWeight: 700,
              color: myMembership.memberType === 'core' ? '#fff' : '#555',
              background: myMembership.memberType === 'core' ? '#1a1a1a' : '#eee',
              borderRadius: 4, padding: '2px 6px',
              marginLeft: 8, letterSpacing: '0.4px',
            }}>
              {myMembership.memberType === 'core' ? 'CORE' : 'ASSOC'}
            </span>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 18, color: '#bbb', lineHeight: 1, padding: '0 2px',
          }}>✕</button>
        </div>

        {/* 본문 */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
          {/* 설명 */}
          <p style={{ fontSize: 13, color: '#666', lineHeight: 1.75, margin: '0 0 20px' }}>
            {nebData.description}
          </p>

          {/* 채팅방 입장 버튼 */}
          {nebData.linkedChatUrl && (
            <a
              href={nebData.linkedChatUrl}
              target="_blank"
              rel="noreferrer"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                padding: '12px 0', marginBottom: 24,
                background: '#000', color: '#fff',
                borderRadius: 12, textDecoration: 'none',
                fontSize: 13, fontWeight: 600, letterSpacing: '0.2px',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
              채팅방 입장 ✦
            </a>
          )}

          {loading ? (
            <div style={{ textAlign: 'center', padding: '24px 0', color: '#ccc', fontSize: 13 }}>
              멤버 목록 불러오는 중...
            </div>
          ) : (
            <>
              {/* Core 멤버 */}
              {coreMembers.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{
                    fontSize: 11, fontWeight: 700, color: '#1a1a1a',
                    letterSpacing: '0.8px', marginBottom: 4,
                  }}>
                    CORE · {coreMembers.length}명
                  </div>
                  {coreMembers.map((m, i) => <MemberRow key={i} member={m} />)}
                </div>
              )}

              {/* Associate 멤버 */}
              {assocMembers.length > 0 && (
                <div>
                  <div style={{
                    fontSize: 11, fontWeight: 700, color: '#888',
                    letterSpacing: '0.8px', marginBottom: 4,
                  }}>
                    ASSOCIATE · {assocMembers.length}명
                  </div>
                  {assocMembers.map((m, i) => <MemberRow key={i} member={m} />)}
                </div>
              )}

              {members.length === 0 && (
                <div style={{ textAlign: 'center', padding: '16px 0', color: '#ccc', fontSize: 13 }}>
                  첫 멤버가 되었어요 ✦
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── NebJoinModal ─────────────────────────────────────────────────

function NebJoinModal({ neb, onClose, user, onJoined }) {
  const [phase, setPhase] = useState('intro');
  const [appId, setAppId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const startInterview = async () => {
    setLoading(true);
    try {
      const idToken = await user.getIdToken();
      const data = await applyNeb(neb.id, idToken);
      if (data.error) {
        if (data.memberType) {
          setResult({ status: 'already', message: data.error });
          setPhase('result');
          return;
        }
        throw new Error(data.error);
      }
      setAppId(data.appId);
      setMessages([{ role: 'assistant', content: data.message }]);
      setPhase('chat');
    } catch (e) {
      alert('연결 오류가 발생했어요. 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  };

  const sendAnswer = async () => {
    if (!input.trim() || loading) return;
    const answer = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: answer }]);
    setLoading(true);
    try {
      const idToken = await user.getIdToken();
      const data = await respondNeb(neb.id, appId, answer, idToken);
      if (data.decided) {
        setMessages(prev => [...prev, { role: 'assistant', content: data.message }]);
        setResult({ status: data.status, message: data.message, suggestedNeb: data.suggestedNeb });
        // 승인 시 부모에 알림
        if (data.status === 'core' || data.status === 'associate') {
          onJoined(neb.id, data.status);
        }
        setTimeout(() => setPhase('result'), 1000);
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: data.message }]);
      }
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', content: '앗, 오류가 생겼어요. 다시 시도해주세요.' }]);
    } finally {
      setLoading(false);
    }
  };

  const symbol = CATEGORY_SYMBOL[neb.category] || '✦';

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 2000,
      background: 'rgba(0,0,0,0.35)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '20px 16px',
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: '#fff', borderRadius: 20,
        width: '100%', maxWidth: 420, maxHeight: '85vh',
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 8px 40px rgba(0,0,0,0.14)',
      }}>
        {/* 헤더 */}
        <div style={{
          padding: '18px 20px 14px',
          borderBottom: '1px solid #f0f0f0',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <span style={{ fontSize: 15, fontWeight: 700 }}>{symbol} {neb.name}</span>
            <span style={{ fontSize: 12, color: '#aaa', marginLeft: 8 }}>AI 심사</span>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 18, color: '#bbb', lineHeight: 1, padding: '0 2px',
          }}>✕</button>
        </div>

        {/* 본문 */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>

          {phase === 'intro' && (
            <div style={{ padding: '4px 0 8px' }}>
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <div style={{ fontSize: 36, marginBottom: 10 }}>{symbol}</div>
                <p style={{ fontSize: 14, color: '#444', lineHeight: 1.8, margin: 0 }}>
                  AI 운영진이 3개 질문으로<br />넵 적합성을 판단해요.
                </p>
              </div>

              {/* 멤버십 가이드 */}
              <div style={{
                background: '#f8f8f8', borderRadius: 12,
                padding: '14px 16px', marginBottom: 20,
                display: 'flex', flexDirection: 'column', gap: 10,
              }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <span style={{
                    fontSize: 10, fontWeight: 700, color: '#fff',
                    background: '#1a1a1a', borderRadius: 4,
                    padding: '2px 7px', flexShrink: 0, marginTop: 1,
                  }}>CORE</span>
                  <p style={{ fontSize: 12, color: '#555', margin: 0, lineHeight: 1.6 }}>
                    해당 분야 실무 경험이 명확하고, 넵에 구체적으로 기여할 의지가 있는 분
                  </p>
                </div>
                <div style={{ height: 1, background: '#eee' }} />
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <span style={{
                    fontSize: 10, fontWeight: 700, color: '#555',
                    background: '#e8e8e8', borderRadius: 4,
                    padding: '2px 7px', flexShrink: 0, marginTop: 1,
                  }}>ASSOC</span>
                  <p style={{ fontSize: 12, color: '#555', margin: 0, lineHeight: 1.6 }}>
                    다른 분야지만 협업 목적이 명확한 분. "그냥 궁금해서"는 어려워요.
                  </p>
                </div>
              </div>

              <p style={{ fontSize: 11, color: '#bbb', textAlign: 'center', marginBottom: 16 }}>
                직무 · 지원 이유 · 기여 계획 — 세 가지를 미리 생각해오세요 ✦
              </p>

              <button
                onClick={startInterview}
                disabled={loading}
                style={{
                  width: '100%', padding: '12px 0', background: '#000', color: '#fff',
                  border: 'none', borderRadius: 12, fontSize: 14, fontWeight: 600,
                  cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1,
                }}
              >
                {loading ? '연결 중...' : '심사 시작하기'}
              </button>
            </div>
          )}

          {phase === 'chat' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {messages.map((m, i) => (
                <div key={i} style={{
                  display: 'flex',
                  justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
                }}>
                  <div style={{
                    maxWidth: '80%', padding: '10px 14px',
                    borderRadius: m.role === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                    background: m.role === 'user' ? '#000' : '#f4f4f4',
                    color: m.role === 'user' ? '#fff' : '#222',
                    fontSize: 13, lineHeight: 1.7,
                  }}>
                    {m.content}
                  </div>
                </div>
              ))}
              {loading && (
                <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <ThinkingBubble />
                </div>
              )}
              <div ref={bottomRef} />
            </div>
          )}

          {phase === 'result' && result && (
            <ResultScreen result={result} onClose={onClose} />
          )}
        </div>

        {phase === 'chat' && (
          <div style={{
            padding: '12px 16px',
            borderTop: '1px solid #f0f0f0',
            display: 'flex', gap: 8,
          }}>
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendAnswer(); } }}
              placeholder="답변을 입력해주세요..."
              disabled={loading}
              style={{
                flex: 1, padding: '10px 14px',
                border: '1px solid #e8e8e8', borderRadius: 10,
                fontSize: 13, outline: 'none',
                background: loading ? '#fafafa' : '#fff',
              }}
            />
            <button
              onClick={sendAnswer}
              disabled={!input.trim() || loading}
              style={{
                padding: '10px 16px', background: '#000', color: '#fff',
                border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 600,
                cursor: !input.trim() || loading ? 'not-allowed' : 'pointer',
                opacity: !input.trim() || loading ? 0.4 : 1, flexShrink: 0,
              }}
            >
              전송
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── NebsPage ─────────────────────────────────────────────────────

export default function NebsPage() {
  const { user } = useAuth();
  const [nebs, setNebs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedNeb, setSelectedNeb] = useState(null);       // join modal
  const [detailNeb, setDetailNeb] = useState(null);           // { neb, membership }
  const [myMemberships, setMyMemberships] = useState([]);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [pendingNeb, setPendingNeb] = useState(null);

  const myNebIds = myMemberships.map(m => m.nebId);

  useEffect(() => {
    listNebs()
      .then(d => setNebs(d.nebs || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // 로그인 시 내 넵 목록 로드
  useEffect(() => {
    if (!user) return;
    user.getIdToken().then(token =>
      getMyNebs(token)
        .then(d => setMyMemberships(d.memberships || []))
        .catch(() => {})
    );
  }, [user]);

  // 로그인 후 pendingNeb 자동 실행
  useEffect(() => {
    if (user && pendingNeb) {
      setPendingNeb(null);
      setSelectedNeb(pendingNeb);
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleJoin = (neb) => {
    if (!user) {
      setPendingNeb(neb);
      setShowLoginModal(true);
      return;
    }
    setSelectedNeb(neb);
  };

  const handleView = (neb, membership) => {
    setDetailNeb({ neb, membership });
  };

  // 승인 콜백 — 카드 즉시 갱신
  const handleJoined = (nebId, memberType) => {
    setMyMemberships(prev => [...prev, { nebId, memberType: memberType || 'associate' }]);
  };

  return (
    <div style={{ minHeight: '100vh', background: '#fafafa' }}>
      <div style={{
        borderBottom: '1px solid #ebebeb', background: '#fff',
        padding: '14px 20px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <a href="/" style={{ color: '#333', textDecoration: 'none', fontSize: 13 }}>← 명함으로</a>
        <span style={{ fontSize: 15, fontWeight: 700, letterSpacing: '-0.3px' }}>✦ Neb</span>
        <div style={{ width: 60 }} />
      </div>

      <div style={{ maxWidth: 680, margin: '0 auto', padding: '28px 16px 60px' }}>
        <div style={{ marginBottom: 28 }}>
          <h2 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 6px', letterSpacing: '-0.5px' }}>
            어떤 넵에 합류할까요?
          </h2>
          <p style={{ fontSize: 13, color: '#999', margin: 0 }}>
            AI 심사를 통해 나에게 맞는 넵에 참여할 수 있어요
          </p>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#ccc', fontSize: 13 }}>
            넵 불러오는 중...
          </div>
        ) : nebs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#ccc', fontSize: 13 }}>
            아직 열린 넵이 없어요
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 16,
          }}>
            {nebs.map(neb => (
              <NebCard
                key={neb.id}
                neb={neb}
                onJoin={handleJoin}
                onView={handleView}
                myMemberships={myMemberships}
              />
            ))}
          </div>
        )}
      </div>

      {selectedNeb && (
        <NebJoinModal
          neb={selectedNeb}
          user={user}
          onClose={() => setSelectedNeb(null)}
          onJoined={handleJoined}
        />
      )}

      {detailNeb && (
        <NebDetailModal
          neb={detailNeb.neb}
          myMembership={detailNeb.membership}
          onClose={() => setDetailNeb(null)}
        />
      )}

      {showLoginModal && (
        <LoginModal
          show={showLoginModal}
          onHide={() => setShowLoginModal(false)}
          reason="neb"
        />
      )}
    </div>
  );
}
